import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef } from 'react';
import styled from 'styled-components';
import ndsTokens from 'nicollet-tokens';
import uniqueId from 'lodash/uniqueId';
import { ScreenReaderOnly } from '@nicollet/common';
import styles from './styles.js';

const colorSwitch = spinnerColor => {
  switch (spinnerColor) {
    case 'white':
      return ndsTokens.colors.palette.white;

    case 'green':
      return ndsTokens.colors.palette.green.light;

    default:
      return ndsTokens.colors.palette.gray.medium;
  }
};

const Spinner = ({
  className,
  id,
  layout = 'block',
  size = 'medium',
  spinnerColor,
  ...restProps
}) => {
  // The requirement for a unique ID is because of three things, which together can create problems:
  // 1. We use inline SVG, because inline SVG is awesome.
  // 2. Gradients are held in the <defs> block and referred to by ID.
  // 3. IDs are global to a page, so two identical IDs will create a collision and undefined behavior.
  //
  // The issue is that if there are ever two spinners on a page with different colors (god forbid),
  // they'll randomly grab at each other's gradients and be messed up. Therefore, broadly, all IDs
  // present in inline SVGs must be prefixed to be unqiue.
  const color = colorSwitch(spinnerColor);
  const instanceIdRef = useRef(uniqueId('nds-spinner'));
  const idPrefix = id !== null && id !== void 0 ? id : instanceIdRef.current;
  return jsxs("div", Object.assign({
    className: className,
    id: id
  }, restProps, {
    children: [jsx(ScreenReaderOnly, Object.assign({
      role: "alert"
    }, {
      children: "Loading, please wait..."
    }), void 0), jsxs("svg", Object.assign({
      version: "1.1",
      viewBox: "0 0 24 24"
    }, {
      children: [jsxs("defs", {
        children: [jsxs("linearGradient", Object.assign({
          gradientUnits: "userSpaceOnUse",
          id: `${idPrefix}-gradientSegment1`,
          x1: "10.818",
          x2: "10.759",
          y1: "1.666",
          y2: "10.619"
        }, {
          children: [jsx("stop", {
            offset: "0",
            stopColor: color,
            stopOpacity: ".8"
          }, void 0), jsx("stop", {
            offset: "1",
            stopColor: color,
            stopOpacity: ".4"
          }, void 0)]
        }), void 0), jsxs("linearGradient", Object.assign({
          gradientUnits: "userSpaceOnUse",
          id: `${idPrefix}-gradientSegment2`,
          x1: "1.8156",
          x2: "4.11",
          y1: "13.121",
          y2: "18.952"
        }, {
          children: [jsx("stop", {
            offset: "0",
            stopColor: color,
            stopOpacity: ".4"
          }, void 0), jsx("stop", {
            offset: "1",
            stopColor: color,
            stopOpacity: ".2"
          }, void 0)]
        }), void 0), jsxs("linearGradient", Object.assign({
          gradientUnits: "userSpaceOnUse",
          id: `${idPrefix}-gradientSegment3`,
          x1: "5.2219",
          x2: "11.097",
          y1: "19.854",
          y2: "22.45"
        }, {
          children: [jsx("stop", {
            offset: "0",
            stopColor: color,
            stopOpacity: ".2"
          }, void 0), jsx("stop", {
            offset: "1",
            stopColor: color,
            stopOpacity: "0"
          }, void 0)]
        }), void 0)]
      }, void 0), jsxs("g", Object.assign({
        fill: "none",
        strokeWidth: "3"
      }, {
        children: [jsx("path", Object.assign({
          d: "m12 1.564c-5.764 0-10.436 4.672-10.436 10.436",
          stroke: `url(#${idPrefix}-gradientSegment1)`
        }, {
          children: jsxs("linearGradient", Object.assign({
            gradientUnits: "userSpaceOnUse",
            x1: "10.818",
            x2: "10.759",
            y1: "1.666",
            y2: "10.619"
          }, {
            children: [jsx("stop", {
              offset: "0",
              stopColor: color,
              stopOpacity: ".8"
            }, void 0), jsx("stop", {
              offset: "1",
              stopColor: color,
              stopOpacity: ".4"
            }, void 0)]
          }), void 0)
        }), void 0), jsx("path", Object.assign({
          d: "m1.564 12c0 2.882 1.168 5.49 3.057 7.38",
          stroke: `url(#${idPrefix}-gradientSegment2)`
        }, {
          children: jsxs("linearGradient", Object.assign({
            gradientUnits: "userSpaceOnUse",
            x1: "1.8156",
            x2: "4.11",
            y1: "13.121",
            y2: "18.952"
          }, {
            children: [jsx("stop", {
              offset: "0",
              stopColor: color,
              stopOpacity: ".4"
            }, void 0), jsx("stop", {
              offset: "1",
              stopColor: color,
              stopOpacity: ".2"
            }, void 0)]
          }), void 0)
        }), void 0), jsx("path", Object.assign({
          d: "M4.62 19.38A10.402 10.402 0 0 0 12 22.435",
          stroke: `url(#${idPrefix}-gradientSegment3)`
        }, {
          children: jsxs("linearGradient", Object.assign({
            gradientUnits: "userSpaceOnUse",
            x1: "5.2219",
            x2: "11.097",
            y1: "19.854",
            y2: "22.45"
          }, {
            children: [jsx("stop", {
              offset: "0",
              stopColor: color,
              stopOpacity: ".2"
            }, void 0), jsx("stop", {
              offset: "1",
              stopColor: color,
              stopOpacity: "0"
            }, void 0)]
          }), void 0)
        }), void 0)]
      }), void 0)]
    }), void 0)]
  }), void 0);
};

Spinner.displayName = 'Spinner'; // String is used in debugging messages.

const StyledSpinner = styled(Spinner)`
  ${styles};
`;
StyledSpinner.displayName = 'Spinner';

export { StyledSpinner as Spinner };
